import * as React from "react";
import { createContext, useContext, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { AlertToast } from "@/components/ui/alert-toast";

type ToastVariant = "success" | "warning" | "info" | "error";

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DEFAULT_DURATION = 5000;

export function AlertToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { ...toast, id };
    
    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration
    const duration = toast.duration ?? DEFAULT_DURATION;
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const success = useCallback((title: string, description?: string) => {
    addToast({ title, description, variant: "success" });
  }, [addToast]);

  const error = useCallback((title: string, description?: string) => {
    addToast({ title, description, variant: "error" });
  }, [addToast]);

  const warning = useCallback((title: string, description?: string) => {
    addToast({ title, description, variant: "warning" });
  }, [addToast]);

  const info = useCallback((title: string, description?: string) => {
    addToast({ title, description, variant: "info" });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      
      {/* Toast Container */}
      <div 
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex flex-col-reverse gap-3 w-full max-w-sm px-4"
        dir="rtl"
      >
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <AlertToast
              key={toast.id}
              variant={toast.variant}
              styleVariant="filled"
              title={toast.title}
              description={toast.description || ""}
              duration={toast.duration ?? DEFAULT_DURATION}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useAlertToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useAlertToast must be used within an AlertToastProvider");
  }
  return context;
}

// Singleton toast object for easy imports (similar to sonner's toast)
let toastFunctions: ToastContextType | null = null;

export function setToastFunctions(functions: ToastContextType) {
  toastFunctions = functions;
}

export const alertToast = {
  success: (title: string, description?: string) => {
    toastFunctions?.success(title, description);
  },
  error: (title: string, description?: string) => {
    toastFunctions?.error(title, description);
  },
  warning: (title: string, description?: string) => {
    toastFunctions?.warning(title, description);
  },
  info: (title: string, description?: string) => {
    toastFunctions?.info(title, description);
  },
};
